import { QueryClient } from "@tanstack/react-query";

// In dev, requests use relative paths and Vite proxies /api/* to the Django
// backend (see vite.config.ts). In production the portal (portal.taxr.io)
// is cross-origin with the API (api.taxr.io), so we target the absolute
// host — the backend has CORS_ALLOW_CREDENTIALS and the session cookie is
// SameSite=None; Secure. VITE_API_URL overrides both (staging, local prod
// builds, etc).
export const API_URL =
  import.meta.env.VITE_API_URL ??
  (import.meta.env.PROD ? "https://api.taxr.io" : "");

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 1000 * 60 * 5, retry: 1 },
  },
});

export const NETWORK_DOWN_MESSAGE =
  "Network is down. Refresh your page or try again shortly.";

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export function isNetworkError(err: unknown): boolean {
  return (
    err instanceof ApiError && (err.status === 0 || err.status >= 502)
  );
}

/**
 * Runs `fn` and, if it throws a transient/network-class ApiError, retries
 * exactly once after a short delay. 4xx and other ApiErrors are never
 * retried — those are user-fixable (cap reached, bad file, auth) and we
 * want the accountant to see them immediately. Keeps the accountant portal
 * predictable: one silent recovery attempt, then surface.
 */
export async function retryOnceOnNetwork<T>(
  fn: () => Promise<T>,
  { delayMs = 500 }: { delayMs?: number } = {},
): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (!isNetworkError(err)) throw err;
    await new Promise((r) => setTimeout(r, delayMs));
    return fn();
  }
}

// Guard so that rapid concurrent 401/403 responses only trigger one redirect.
// Without this, every in-flight request fires window.location.href = '/login'
// simultaneously; the subsequent throws still reach each caller's .catch so
// pending toasts can finalise normally.
let redirecting = false;

export async function apiFetch<T>(
  path: string,
  options?: RequestInit
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, {
      ...options,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...options?.headers,
      },
    });
  } catch {
    throw new ApiError(0, NETWORK_DOWN_MESSAGE);
  }

  if (!res.ok) {
    if (res.status === 401 || res.status === 403) {
      if (!redirecting) {
        redirecting = true;
        window.location.href = "/login";
      }
      throw new ApiError(res.status, "Unauthorized");
    }
    if (res.status >= 502 && res.status <= 504) {
      throw new ApiError(res.status, NETWORK_DOWN_MESSAGE);
    }
    const body = await res.json().catch(() => ({}));
    throw new ApiError(
      res.status,
      body?.detail || body?.message || `API error: ${res.status}`
    );
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

export function apiGet<T>(path: string) {
  return apiFetch<T>(path, { method: "GET" });
}

export function apiPost<T>(path: string, body?: unknown) {
  return apiFetch<T>(path, {
    method: "POST",
    body: body ? JSON.stringify(body) : undefined,
  });
}

export function apiPatch<T>(path: string, body?: unknown) {
  return apiFetch<T>(path, {
    method: "PATCH",
    body: body ? JSON.stringify(body) : undefined,
  });
}

export function apiDelete<T>(path: string) {
  return apiFetch<T>(path, { method: "DELETE" });
}
